import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Music24LicenseStateType } from 'fym-common/src/license/music24Types';
import { isPaidTier } from 'fym-shared/src/music24/onboardingChecklist';
import { TypedMemo } from '../../common/types';
import { SMLContainer } from '../../smartLinks/smlContainer/SMLContainer';
import { SMLBreadcrumbs } from '../../smartLinks/smlBreadcrumbs/SMLBreadcrumbs';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { useUser } from '../../users/hooks/useUser';
import { useTRPCClientAPI } from '../../../trpc/trpc';
import { useUserToken } from '../../../redux/slices/appInfo/hooks';
import { setLicenseInfoResult } from '../../../redux/slices/license/slice';
import { clearCheckoutIntent } from '../config/checkoutIntent';
import { fbqTrack } from '../utils/facebookPixel';
import { CreditPackComplete } from './CreditPackComplete';
import { useMusic24Plan } from '../hooks/useMusic24Plan';
import { readPaymentWatermarkSummary } from '../watermarkUpsell/paymentWatermarkSummary';
import { usePurchaseCompletedEvent } from '../watermarkUpsell/usePurchaseCompletedEvent';
import { PaymentStateView } from './PaymentStateViews';
import {
  CheckoutSessionState,
  checkoutSessionStateOf,
  finalPaymentStatusOf,
  PaymentPageStatus,
} from './checkoutSessionState';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_TIME_MS = 30000;
/** Reads that fail in a row before the page stops: a wrong or fake session id fails every time. */
const MAX_FAILED_READS = 3;

/**
 * The return from a plan checkout (QA finding brk-8). The page says "Your subscription is active"
 * only when `billing.verifyCheckoutSession` confirms a paid checkout: a trial licence is valid too,
 * so the licence alone proves nothing. A wrong session id or an unpaid checkout shows "We could not
 * confirm the payment"; a link with no session id shows "No payment to confirm", except for a user
 * who has a paid plan.
 */
const SubscriptionPaymentComplete: React.FC = () => {
  const intl = useIntl();
  const router = useSMLRouter();
  const dispatch = useDispatch();
  const { refetch } = useUser();
  const trpcClient = useTRPCClientAPI();
  const userToken = useUserToken();
  const plan = useMusic24Plan();

  const sessionId = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('session_id');
  }, []);

  const [status, setStatus] = useState<PaymentPageStatus>(() => (sessionId === null ? 'noSession' : 'checking'));
  const [purchaseFired, setPurchaseFired] = useState(false);
  const [amountTotal, setAmountTotal] = useState<number | null>(null);
  const [currency, setCurrency] = useState<string | null>(null);
  // The watermark summary of before the payment, read before the summary of this page can replace it.
  const [summaryBefore] = useState(readPaymentWatermarkSummary);

  // A link with no session id has nothing to verify. A user with a paid plan still sees the welcome.
  useEffect(() => {
    if (sessionId === null && isPaidTier(plan)) {
      setStatus('success');
    }
  }, [plan, sessionId]);

  // For unauthenticated users: get email from Stripe and redirect to auth
  useEffect(() => {
    if (userToken !== null || sessionId === null) {
      return;
    }

    (async () => {
      const session = await trpcClient.billing.verifyCheckoutSession.query({ sessionId });
      if (session.customerEmail !== null) {
        const returnTo = `paymentComplete?session_id=${encodeURIComponent(sessionId)}`;
        router.replace(SMLRoutes.auth, {
          email: session.customerEmail,
          returnTo,
        });
        return;
      }
      setStatus('failed');
    })().catch((error) => {
      console.error(error);
      setStatus('failed');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userToken === null || status !== 'checking' || sessionId === null) {
      return;
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let elapsed = 0;
    let failedReads = 0;
    let lastState: CheckoutSessionState | null = null;

    const poll = async () => {
      try {
        const session = await trpcClient.billing.verifyCheckoutSession.query({ sessionId });
        if (cancelled) {
          return;
        }
        failedReads = 0;
        lastState = checkoutSessionStateOf(session);
        if (session.amountTotal !== null && session.currency !== null) {
          setAmountTotal(session.amountTotal);
          setCurrency(session.currency);
        }
        if (session.licenseInfo !== null) {
          dispatch(setLicenseInfoResult(session.licenseInfo as Music24LicenseStateType));
        }
        if (lastState === 'paid') {
          await refetch();
          if (cancelled) {
            return;
          }
          clearCheckoutIntent();
          setStatus('success');
          return;
        }
      } catch (error) {
        // A wrong or fake session id fails every read ("No such checkout.session"). A network blip
        // fails one read. Stop after a few failed reads in a row.
        console.error(error);
        failedReads += 1;
        if (failedReads >= MAX_FAILED_READS && lastState === null) {
          if (!cancelled) {
            setStatus('failed');
          }
          return;
        }
      }
      if (cancelled) {
        return;
      }

      elapsed += POLL_INTERVAL_MS;
      if (elapsed >= MAX_POLL_TIME_MS) {
        setStatus(finalPaymentStatusOf(lastState));
        return;
      }
      timeoutId = setTimeout(() => {
        poll().catch(console.error);
      }, POLL_INTERVAL_MS);
    };

    poll().catch(console.error);

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [userToken, status, sessionId, trpcClient, dispatch, refetch]);

  useEffect(() => {
    if (
      status === 'success' &&
      !purchaseFired &&
      sessionId !== null &&
      amountTotal !== null &&
      amountTotal > 0 &&
      currency !== null
    ) {
      fbqTrack('Purchase', { value: amountTotal, currency }, sessionId);
      setPurchaseFired(true);
    }
  }, [status, purchaseFired, sessionId, amountTotal, currency]);

  // `purchase_completed`, with the upsell surface that started the checkout (trial upsell §8).
  usePurchaseCompletedEvent({ isSuccess: status === 'success' && sessionId !== null, plan, amountTotal, currency });

  const handleContinue = useCallback(() => {
    router.push(SMLRoutes.home);
  }, [router]);
  const openPlans = useCallback(() => {
    router.push(SMLRoutes.plans);
  }, [router]);

  const breadcrumbs = [
    {
      name: intl.formatMessage({
        id: 'app.sml.payment.complete.breadcrumb',
        defaultMessage: 'Payment',
      }),
      onClick: handleContinue,
    },
  ];

  return (
    <SMLContainer header={<SMLBreadcrumbs breadcrumbs={breadcrumbs} />} hidePricingModal>
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <PaymentStateView
          status={status}
          summaryBefore={summaryBefore}
          onContinue={handleContinue}
          onOpenPlans={openPlans}
        />
      </div>
    </SMLContainer>
  );
};

/**
 * The return from a Stripe Checkout. A credit pack (`creditPack=1`) goes back to the page the user
 * bought from (`CreditPackComplete`); a plan shows the subscription states. Outreach v2 sells no
 * pitch pack.
 */
const PaymentCompleteViewComponent: React.FC = () => {
  const intl = useIntl();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  if (params.get('creditPack') !== '1') {
    return <SubscriptionPaymentComplete />;
  }
  const breadcrumbs = [
    { name: intl.formatMessage({ id: 'app.sml.payment.complete.breadcrumb', defaultMessage: 'Payment' }) },
  ];
  return (
    <SMLContainer header={<SMLBreadcrumbs breadcrumbs={breadcrumbs} />} hidePricingModal>
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <CreditPackComplete sessionId={params.get('session_id')} />
      </div>
    </SMLContainer>
  );
};

export const PaymentCompleteView = TypedMemo(PaymentCompleteViewComponent);
